/**
 * <exchange-life-notification> - global toast custom element.
 *
 * Listens document-wide for `exchange-life:notify` CustomEvents and shows a short-lived,
 * non-blocking message. Replaces native alert() (silently suppressed by browsers on repeated
 * rapid calls - a confirmed dead-click/rage-click source on mobile, e.g. bundle-builder's
 * Next/Add to Cart validation) and any form's own inline error banner state - one element,
 * mounted once (see snippets/notification-toast.liquid, rendered globally in layout/theme.liquid),
 * triggered from anywhere via a plain DOM event instead of framework props/context.
 *
 * detail: { message: string, type?: 'error' | 'success' | 'info', duration?: number (ms) }
 *
 * `exchange-life:notify-dismiss` hides the toast early - detail: { message?: string }. With a
 * message, only hides if that exact message is the one currently showing (so e.g. clearing a
 * stale "select a charm" warning never swallows an unrelated toast shown after it).
 */
(function () {
  if (typeof window === "undefined" || typeof customElements === "undefined") return;

  class ExchangeLifeNotification extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector(".el-notification")) {
        const box = document.createElement("div");
        box.className = "el-notification";
        box.setAttribute("role", "alert");
        box.setAttribute("aria-live", "assertive");
        box.hidden = true;

        const text = document.createElement("span");
        text.className = "el-notification__message";
        box.appendChild(text);

        const close = document.createElement("button");
        close.type = "button";
        close.className = "el-notification__close text-main-text";
        close.setAttribute("aria-label", this.dataset.closeLabel || "Close");
        close.innerHTML =
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        box.appendChild(close);

        this.appendChild(box);
      }
      this.box = this.querySelector(".el-notification");
      this.message = this.box.querySelector(".el-notification__message");
      this.box.querySelector(".el-notification__close").addEventListener("click", () => this.dismiss());
      this.hideTimer = null;

      this.handleNotify = (event) => this.show(event.detail || {});
      document.addEventListener("exchange-life:notify", this.handleNotify);

      this.handleDismiss = (event) => this.dismiss(event.detail || {});
      document.addEventListener("exchange-life:notify-dismiss", this.handleDismiss);
    }

    disconnectedCallback() {
      document.removeEventListener("exchange-life:notify", this.handleNotify);
      document.removeEventListener("exchange-life:notify-dismiss", this.handleDismiss);
      if (this.hideTimer) clearTimeout(this.hideTimer);
    }

    show({ message, type = "error", duration = 3500 } = {}) {
      if (!message || !this.box) return;

      if (this.hideTimer) clearTimeout(this.hideTimer);

      this.message.textContent = message;
      this.box.classList.remove("is-error", "is-success", "is-info");
      this.box.classList.add(`is-${type}`);
      this.box.hidden = false;
      this.positionNearAnchor();

      // Next frame so the `hidden -> visible` transition actually animates instead of the
      // opacity/transform classes applying before the element has its first paint.
      requestAnimationFrame(() => this.box.classList.add("is-visible"));

      this.hideTimer = setTimeout(() => this.hide(), duration);
    }

    /**
     * Desktop only: if a `[data-notify-anchor]` element (e.g. the bundle builder's Next button
     * group) is on screen, pin the toast right-aligned with it, just above it. Otherwise fall
     * back to the default bottom-center position from notification-toast.liquid.
     */
    positionNearAnchor() {
      this.classList.remove("is-anchored");
      this.style.removeProperty("right");
      this.style.removeProperty("bottom");

      if (!window.matchMedia("(min-width: 1025px)").matches) return;

      const anchor = Array.from(document.querySelectorAll("[data-notify-anchor]")).find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.bottom <= window.innerHeight;
      });
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      this.classList.add("is-anchored");
      this.style.right = `${Math.max(viewportWidth - rect.right, 16)}px`;
      this.style.bottom = `${window.innerHeight - rect.top + 30}px`;
    }

    dismiss({ message } = {}) {
      if (!this.box || this.box.hidden) return;
      if (message && this.message.textContent !== message) return;
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hide();
    }

    hide() {
      if (!this.box) return;
      this.box.classList.remove("is-visible");
      this.hideTimer = setTimeout(() => {
        if (this.box) this.box.hidden = true;
      }, 200);
    }
  }

  if (!customElements.get("exchange-life-notification")) {
    customElements.define("exchange-life-notification", ExchangeLifeNotification);
  }
})();
